import React, { FunctionComponent, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "wouter/use-location";

import {
  ProductTypeTire,
  productTypeAccessory,
  productTypePackage,
  productTypeTire,
  productTypeWheel,
} from "../../constants/productTypes";
import { DealerVehicleResultsRoute } from "../../navigation/DealerRoutes";
import { useRouteParams } from "../../navigation/useRouteParams";
import { useUrl } from "../../stores/hooks/useUrl";
import { HashtagString } from "../../stores/useHashState";
import { Button } from "../../ui/Button/Button";
import { Dialog } from "../../ui/Dialog/Dialog";
import { ArrowLeftIcon } from "../../ui/icons/ArrowLeftIcon";
import { AccessoryModalContent } from "./Accessory/AccessoryModalContent";
import { PackageModalContent } from "./Package/PackageModalContent";
import { TireModalContent } from "./Tire/TireModalContent";
import { WheelModalContent } from "./Wheel/WheelModalContent";

export const getProductModalHash = (itemId?: string): HashtagString =>
  `#product-${itemId}`;

export const useProductModalHash = (): {
  handleClose: VoidFunction;
  itemId: null | string;
  visible: boolean;
} => {
  const { hash, pathname, search } = useUrl();
  const [key, itemId] = hash.split("-");

  const handleClose = useCallback(() => {
    navigate(`${pathname}${search}`);
  }, [origin, pathname, search]);

  return {
    handleClose,
    itemId: itemId || null,
    visible: key === "#product",
  };
};

const componentsMap = {
  [productTypeAccessory]: AccessoryModalContent,
  [productTypePackage]: PackageModalContent,
  [productTypeTire]: TireModalContent,
  [productTypeWheel]: WheelModalContent,
};

const Uncached: FunctionComponent = () => {
  const params = useRouteParams(DealerVehicleResultsRoute);
  const { handleClose, visible } = useProductModalHash();
  const { t } = useTranslation();

  const Component =
    componentsMap[String(params?.productType) as ProductTypeTire] ||
    TireModalContent;
  return (
    <Dialog
      className="p-4 md:p-8 md:w-full md:max-w-[64rem]"
      id="product-modal"
      onClose={handleClose}
      visible={visible}
    >
      <Button
        aria-label="backToSearchResults"
        className="-ml-2 md:-ml-4 mb-2 font-thin"
        onClick={handleClose}
      >
        <ArrowLeftIcon />
        {t("searchResults.backToResults")}
      </Button>
      <Component />
    </Dialog>
  );
};

export const ProductModal = memo(Uncached);
